import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import Layout from 'components/common/layout/Layout';
import SearchFilter from 'components/common/forms/SearchFilter';
import ChartSwiper from 'components/common/lists/ChartSwiper';
import BarChart from 'components/pages/common/BarChart';
import TableForm from 'components/common/lists/TableForm';
import styles from 'styles/pages/student/ScoreReport.module.scss';
import { ReactComponent as CircleCheckIcon } from 'assets/icons/circle_check.svg';
import { ReactComponent as CircleCheckOnIcon } from 'assets/icons/circle_check_on.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron_right.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ROUTE_PATH } from 'constants/routePath';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TTableColumn } from 'types/components/table';
import { TScoreReportInfo, TScoreReportResponse } from 'types/apis/student';
import { TChartGradingStatus, TScoreByChart } from 'types/chart';
import { getScoreReport } from 'apis/student';
import { getScoreLabel, getScoreColor } from 'utils/score';
import useModal from 'hooks/useModal';

const tableColumns: TTableColumn[] = [
  {
    name: 'no',
    label: '번호',
    width: '90px',
  },
  {
    name: 'chartTypeName',
    label: '차트 종류',
    width: '280px',
  },
  {
    name: 'professorName',
    label: '평가자',
    width: '150px',
  },
  { name: '', label: '1차 상세보기', width: '175px' },
  { name: '', label: '2차 상세보기', width: '175px' },
  { name: 'scoreAvg', label: '평균 점수', width: '150px' },
];

const ScoreReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const { openModal } = useModal();

  const resultDataDefaultValues = ChartInfo.map((chart) => ({
    chartName: chart.name,
    score: null,
  }));

  const [params, setParams] = useState({
    courseInfoId: Number(query.get('courseInfoId')) || undefined,
    chartRound: Number(query.get('chartRound')) || undefined,
  });
  const [scoreReportData, setScoreReportData] = useState<TScoreReportResponse>(
    []
  );
  const [statusData, setStatusData] = useState<
    TChartGradingStatus[] | undefined
  >();
  const [resultData, setResultData] = useState<TScoreByChart[]>(
    resultDataDefaultValues
  );

  useEffect(() => {
    setParams({
      ...params,
      courseInfoId: Number(query.get('courseInfoId')) || undefined,
      chartRound: Number(query.get('chartRound')) || undefined,
    });
  }, [location.search]);

  useQuery(['getScoreReport', params], () => getScoreReport(params), {
    enabled: !!params.courseInfoId && !!params.chartRound,
    onSuccess: (data) => {
      setScoreReportData(data?.data || []);
      setStatusData(
        data?.data.map((item) => ({
          chartName: item.chartTypeName,
          status:
            item.evaluateStatus === 'EVALUATED'
              ? 2
              : item.evaluateStatus === 'SUBMITTED'
              ? 1
              : 0,
        }))
      );
      setResultData(
        data?.data.length > 0
          ? data.data.map((item) => ({
              chartName: item.chartTypeName,
              score: item.scoreAvg || null,
            }))
          : resultDataDefaultValues
      );
    },
  });

  const renderActions = (row: TScoreReportInfo, headerIndex?: number) => {
    const roundInfo = row[headerIndex === 3 ? 'roundOneInfo' : 'roundTwoInfo'];
    const scoreColor =
      headerIndex === 3 || (headerIndex === 4 && roundInfo)
        ? getScoreColor(roundInfo.score)
        : 'GRAY_08';

    return (
      <>
        {(headerIndex === 3 || headerIndex === 4) &&
          (!roundInfo ? (
            '-'
          ) : (
            <div className="flex justify_center">
              <button
                className="flex fs12 fw700 items_center"
                style={{
                  padding: '4px 8px 4px 12px',
                  backgroundColor: STYLES_VARIABLES.WHITE,
                  border: `1px solid ${STYLES_VARIABLES[scoreColor]}`,
                  borderRadius: '50px',
                  color: STYLES_VARIABLES[scoreColor],
                }}
                disabled={
                  !roundInfo.evaluationChartId ||
                  ['대기중', '평가중'].includes(roundInfo.score)
                }
                onClick={() => {
                  const chartRoute = ChartInfo.find(
                    (chart) => chart.type === row.chartType
                  )?.route;
                  if (!chartRoute) {
                    openModal({
                      title: '차트 조회 실패',
                      text: '관리자에게 문의해 주세요.',
                    });
                    return;
                  }
                  sessionStorage.setItem('route', 'report');
                  // 차트 상세 페이지 > 해당 차수의 평가 탭으로 이동
                  navigate(
                    chartRoute &&
                      roundInfo.evaluationChartId &&
                      roundInfo.patientId &&
                      roundInfo.evaluationRound
                      ? `/evaluation${chartRoute}/${
                          roundInfo.evaluationChartId
                        }?patientId=${roundInfo.patientId}&courseInfoId=${
                          params.courseInfoId
                        }&chartRound=${params.chartRound}&tab=${
                          roundInfo.evaluationRound === 2 ? 3 : 1
                        }`
                      : `${ROUTE_PATH.student.EVALUATION_CHART}?courseInfoId=${params.courseInfoId}&chartRound=${params.chartRound}`
                  );
                }}
              >
                {getScoreLabel(roundInfo.score)}
                <ChevronRightIcon
                  width={18}
                  height={18}
                  fill={STYLES_VARIABLES[scoreColor]}
                  style={{ marginLeft: '8px' }}
                />
              </button>
            </div>
          ))}
      </>
    );
  };

  return (
    <Layout>
      <h1 className="main_title mt40 mb24">종합평가</h1>

      <SearchFilter
        dropdown={[
          {
            id: 'courseClassInfo',
            placeholder: '강의명',
            width: '240px',
          },
          {
            id: 'chartRound',
            placeholder: '(회차) 회차명',
            width: '240px',
          },
        ]}
        align="right"
      />

      {/* 평가 현황 기준 */}
      <div className={styles.chart_card_wrapper}>
        <div className={styles.chart_title}>
          <h2 className="fs24 fw700">
            평가 현황 기준 :{' '}
            {params.chartRound ? `${params.chartRound}회차` : '-'}
          </h2>
          <div className="flex items_center justify_between">
            <CircleCheckIcon width={24} height={24} />
            <span className="fs14 ml7 mr16">: 차트 제출 완료</span>
            <CircleCheckOnIcon width={24} height={24} />
            <span className="fs14 ml7">: 차트 제출 및 평가 완료</span>
          </div>
        </div>
        <ChartSwiper data={statusData} />
      </div>

      {/* 차트별 평가 그래프 */}
      <BarChart type="student" data={resultData} />

      {/* 차트별 평가 결과 */}
      <h2 className="main_title mt40 mb24">차트별 평가 결과</h2>
      <TableForm
        data={scoreReportData}
        columns={tableColumns}
        renderActions={renderActions}
        emptyMessage="등록된 차트가 없습니다."
      />
    </Layout>
  );
};

export default ScoreReport;
